import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { WhiteLabelService } from '@rms/core/layout';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { checkRouterChildsData } from '@vex/utils/check-router-childs-data';
import { Observable, of, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';

import { NavigationService } from '../../../core/navigation/navigation.service';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { NavigationComponent } from '../navigation/navigation.component';
import { NavigationItemComponent } from '../navigation/navigation-item/navigation-item.component';
import { MegaMenuComponent } from './mega-menu/mega-menu.component';

@Component({
  selector: 'rms-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    RouterLink,
    MatMenuModule,
    NavigationItemComponent,
    NavigationComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class ToolbarComponent implements OnInit, OnDestroy {
  @HostBinding('class.shadow-b')
  private readonly layoutService = inject(VexLayoutService);

  private readonly configService = inject(VexConfigService);
  private readonly navigationService = inject(NavigationService);
  private readonly popoverService = inject(VexPopoverService);
  private readonly whiteLabelService = inject(WhiteLabelService);
  private readonly router = inject(Router);
  private translateService = inject(TranslateService);
  showShadow = false;

  currentLanguage: string = this.translateService.currentLang;
  languageMap: Record<string, string> = {
    en: 'flag:united-states',
    it: 'flag:italy',
  };

  navigationItems$: Observable<NavigationItem[]> = this.navigationService.items$;

  isHorizontalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'horizontal')
  );

  isVerticalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'vertical')
  );

  isNavbarInToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'in-toolbar')
  );

  isNavbarBelowToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'below-toolbar')
  );

  userVisible$: Observable<boolean> = this.configService.config$.pipe(map((config) => config.toolbar.user.visible));
  title$: Observable<string> = this.configService.select((config) => config.sidenav.title);

  isDesktop$: Observable<boolean> = this.layoutService.isDesktop$;
  megaMenuOpen$: Observable<boolean> = of(false);

  public isLogoEnabled$: Observable<boolean> = this.whiteLabelService.isLogoEnabled$;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private langChangeSubscription: Subscription;

  constructor() {
    this.currentLanguage = this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || 'en';
    this.langChangeSubscription = this.translateService.onLangChange.subscribe((e: LangChangeEvent) => {
      this.currentLanguage = e.lang;
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.showShadow = checkRouterChildsData(
          this.router.routerState.root.snapshot,
          (data) => data.toolbarShadowEnabled ?? false
        );
      });
  }

  ngOnDestroy() {
    this.langChangeSubscription.unsubscribe();
  }

  setLanguage(lang: string) {
    this.translateService.use(lang);
  }

  openQuickpanel(): void {
    this.layoutService.openQuickpanel();
  }

  openSidenav(): void {
    this.layoutService.openSidenav();
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        content: MegaMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ],
      })
    ).pipe(
      switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true)
    );
  }

  openSearch(): void {
    this.layoutService.openSearch();
  }
}
